import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Tabs, Tab, TabList, TabPanels, TabPanel } from '@carbon/react';

import { useNavigate } from 'react-router-dom';

import ApplicationsTable from './ApplicationsTable/ApplicationsTable';
import Header from '../../components/Header/Header';
import RegisterExternalService from './RegisterExternalService/RegisterExternalService';
import SortDropDown from '../../components/SortDropdown/SortDropDown';
import FindAndFilterBar from '../../components/FindAndFilterBar/FindAndFilterBar';
import sortData from '../../lib/tableSort';
import {
  getCombinedLabelsForFilter,
  manageFiltersFromStorage,
} from '../../lib/utils';
import {
  ApplicationData,
  AppliedFilter,
  Error500Type,
  NetworkSegment,
  Policy,
  ResourceGroup,
  ServiceData,
} from '../../models/master';

import { getApplications } from '../../controllers/applicationApis';
import { getResourceGroups } from '../../controllers/resourceGroupApi';
import { getNetworkSegments } from '../../controllers/networksegmentsApi';

import {
  getDeploymentEnvs,
  getPartitions,
} from '../../controllers/deploymentEnv';
import { AxiosError } from 'axios';
import Error403Card from '../../components/ErrorState/Error403Card';
import useAnalytics from '../../lib/useAnalytics';
import analyticsData from '../../lib/analyticsEventData';
import { filterTypes } from '../../lib/enums';
import Error500Card from '../../components/ErrorState/Error500Card';
import {
  DEFAULT_NETWORK_SEGMENT_ID,
  defaultNetworkSegmentObject,
} from '../../lib/constants';

import { getPolicies } from '../../controllers/policyApi';

import ServicesTable from './ServicesTable/ServicesTable';

import './ApplicationsContainer.scss';

type LocationState = {
  resourceType: string;
  navigateBack: boolean;
};

type View = 'table' | 'card';

const defaultPermissionMap = {
  applications: true,
  service: true,
  resourceGroup: true,
  deploymentEnv: true,
  networkSegments: true,
};

const ApplicationsContainer = () => {
  const { t } = useTranslation('applicationsContainer');

  const { pageViewed, trackButtonClicked } = useAnalytics();

  const [currentPageNumber, setPageNumber] = useState(1);
  const [currentPageSize, setPageSize] = useState(25);
  const [applicationsData, setApplicationsData] = useState<
    ApplicationData[] | null
  >(null);
  const [customApplicationsData, setCustomApplicationsData] = useState<
    ApplicationData[] | null
  >(null);
  const [showRegisterTearsheet, setShowRegisterTearsheet] = useState(false);
  const [appDataLoading, toggleAppDataLoading] = useState(false);
  const [filteredData, setFilteredData] = useState<ApplicationData[] | []>([]);
  const [filterApplied, setFilterApplied] = useState<AppliedFilter[] | []>([]);
  const [sortKey, setSortKey] = useState('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | 'NONE'>(
    'NONE'
  );
  const [applicationGroupsData, setApplicationGroupsData] = useState<
    ResourceGroup[] | null
  >(null);
  const [networkSegmentsData, setNetworkSegmentsData] = useState<
    NetworkSegment[] | null
  >(null);

  const [servicesData, setServicesData] = useState<ServiceData[] | null>();

  const [filteredServiceData, setFilteredServiceData] = useState<
    ServiceData[] | []
  >([]);

  const [serviceFilterByApp, setServiceFilterByApp] = useState<string[] | null>(
    null
  );

  const [allDepEnvs, setAllDepEnvs] = useState<any>();
  const [allPartitions, setAllPartitions] = useState<any>();
  const [clusterPartitions, setClusterPartitions] = useState<any[]>([]);
  const [permissionMap, setPermissionMap] = useState(defaultPermissionMap);
  const [error500, setError500] = useState<null | Error500Type>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const refreshData = async () => {
    setApplicationsData(null);
    setSortKey('');
    setSortDirection('NONE');
    setServicesData(null);
    //fetch services
    getApplications(true, true)
      .then(async response => {
        let networkSegmentResponse;
        try {
          networkSegmentResponse = await getNetworkSegments();
          setNetworkSegmentsData(networkSegmentResponse as NetworkSegment[]);

          if (!permissionMap.networkSegments) {
            setPermissionMap(permissionMap => ({
              ...permissionMap,
              networkSegments: true,
            }));
          }
        } catch (error) {
          console.error(error);
          const err = error as AxiosError;
          if (err?.response?.status === 403) {
            setPermissionMap(permissionMap => ({
              ...permissionMap,
              networkSegments: false,
            }));
          }
        }

        return { response, networkSegmentResponse };
      })
      .then(async ({ response, networkSegmentResponse }) => {
        setPermissionMap(permissionMap => ({
          ...permissionMap,
          applications: true,
        }));

        if (error500) {
          setError500(null);
        }

        const allServicesData: ServiceData[] = [];

        const policies: Policy[] = await getPolicies(false, false);

        for (const application of response) {
          const applicationServices = application.services.map(
            (srv: {
              resource_id: any;
              name: any;
              ports: { port_number: any; protocol: any }[];
              associated_policies: any;
              network_segment_name: any;
              updated_at: any;
              port_protocol_field: any;
            }) => ({
              id: srv.resource_id,
              name: srv.name,
              ports: srv.ports ?? [],
              port_number: srv.ports[0]?.port_number ?? '',
              protocol: srv.ports[0]?.protocol ?? '',
              port_protocol_field: `${srv.ports[0]?.port_number || ''}/${
                srv.ports[0]?.protocol || ''
              }`,
              application_name: application?.name,
              updated_at: srv.updated_at,
              network_segment_id: application?.network_segment_id,
              network_segment_name: getNetworkSegmentName(
                application?.network_segment_id,
                networkSegmentResponse
              ),
              associated_policies: getAssociatedPolices(
                policies,
                application.resource_id,
                srv.resource_id
              ),
            })
          );

          allServicesData.push(...applicationServices);
        }

        setServicesData(allServicesData);

        setCustomApplicationsData(response as ApplicationData[]);

        getApplicationList();
      })
      .catch(error => {
        setApplicationsData([]);
        toggleAppDataLoading(false);
        if (error.response?.status === 403) {
          setPermissionMap(permissionMap => ({
            ...permissionMap,
            applications: false,
          }));
        }

        if (error.response?.status >= 500) {
          setError500(error.response?.status?.toString());
        }

        console.error(error);
      });

    getResourceGroups(true)
      .then(response => {
        setApplicationGroupsData(
          response.resource_groups.filter(
            (rg: ResourceGroup) => rg.type === 'application'
          ) as ResourceGroup[]
        );
        setPermissionMap(permissionMap => ({
          ...permissionMap,
          resourceGroup: true,
        }));
      })
      .catch(error => {
        if (error.response?.status === 403) {
          setPermissionMap(permissionMap => ({
            ...permissionMap,
            resourceGroup: false,
          }));
        }
        console.error(error);
      });

    setFilterApplied(manageFiltersFromStorage());
  };

  const getApplicationList = () => {
    const applicationList = [
      ...Array.from(
        new Set(servicesData?.map(service => service.application_name))
      ),
    ];
    setServiceFilterByApp(applicationList as string[]);
  };

  const getResourceGroupName = (id: string) => {
    const resouceGroup =
      id &&
      Array.isArray(applicationGroupsData) &&
      applicationGroupsData.find(
        resouceGroup => resouceGroup.resource_id === id
      );
    return resouceGroup ? resouceGroup.name : '—';
  };

  const getNetworkSegmentName = (
    id: string,
    lNetworkSegmentData?: NetworkSegment
  ) => {
    let networkSegmentData = lNetworkSegmentData ?? networkSegmentsData;
    const networkSegments =
      id === DEFAULT_NETWORK_SEGMENT_ID
        ? defaultNetworkSegmentObject
        : id &&
          Array.isArray(networkSegmentData) &&
          networkSegmentData.find(segments => segments.resource_id === id);
    return networkSegments ? networkSegments.name : '—';
  };

  const customCreatedApplicationData = () => {
    let applicationContainerData = customApplicationsData?.map(
      applicationData => ({
        ...applicationData,
        resource_group_name: getResourceGroupName(
          applicationData.resource_group_id
        ),
        network_segment_name: getNetworkSegmentName(
          applicationData.network_segment_id
        ),
        serviceCount: applicationData.serviceCount,
      })
    );
    setApplicationsData(applicationContainerData as ApplicationData[]);
  };

  const removeDuplicateNetworkSegments = () => {
    let networkSegmentElements = new Set(
      applicationsData?.map((item: any) => item.network_segment_name)
    );
    let segmentElements = Array.from(networkSegmentElements);
    let elementIndex = segmentElements.indexOf('—');
    if (elementIndex > -1) {
      segmentElements.splice(elementIndex, 1);
    }
    return segmentElements;
  };

  const leftInlineMultiSelectFilter: any = [
    {
      key: 'applicationNetworkSegment',
      type: 'multi',
      values: removeDuplicateNetworkSegments().map(item => {
        return {
          label: item,
          value: item,
        };
      }) as any,
      placeHolderText: removeDuplicateNetworkSegments().length,
    },
  ];

  const createdService = () => {
    setShowRegisterTearsheet(false);
    refreshData();
    if (state) {
      state.navigateBack = false;
    }
  };

  const closeRegisterTearsheet = () => {
    setShowRegisterTearsheet(false);
    if (state?.navigateBack) {
      navigate(-1);
    }
  };

  const getAssociatedPolices = (
    policies: Policy[],
    application_id: string,
    service_id: string
  ) => {
    let count = 0;

    return policies.filter(
      policy =>
        policy.to.service.application_id === application_id &&
        policy.to.service.service_id === service_id
    );
  };

  const handleSort = (data: { id: string; text: string }) => {
    if (Array.isArray(applicationsData)) {
      if (data.id === 'atoz') {
        setSortDirection('ASC');
        const sortedApplication = Array.isArray(applicationsData)
          ? applicationsData.sort((a, b) =>
              a.name?.trim()?.toLowerCase() > b.name?.trim()?.toLowerCase()
                ? 1
                : -1
            )
          : [];
        setApplicationsData([...sortedApplication]);
      } else {
        setSortDirection('DESC');
        const sortedApplication = Array.isArray(applicationsData)
          ? applicationsData.sort((a, b) =>
              a.name?.trim()?.toLowerCase() < b.name?.trim()?.toLowerCase()
                ? 1
                : -1
            )
          : [];
        setApplicationsData([...sortedApplication]);
      }
    }
  };

  const renderFilter = () => {
    return (
      <FindAndFilterBar
        data={applicationsData ? applicationsData : []}
        filteredDataCallback={data =>
          setFilteredData(data as ApplicationData[] | [])
        }
        persistFilter
        filteredData={
          applicationsData
            ? filterApplied.length > 0
              ? filteredData
              : applicationsData
            : null
        }
        filtersApplied={filterApplied as any}
        filtersAppliedCallback={data => setFilterApplied(data)}
        filters={[
          {
            key: 'applicationGroup',
            type: 'single',
            label: t('filters.applicationGroup'),
            placeHolderText: t('filters.applicationGroupPlaceholder'),
            values: [
              ...Array.from(
                new Set(applicationGroupsData?.map(appGroup => appGroup.name))
              ),
            ],
          },
          {
            key: filterTypes.ALLLABELS,
            type: 'multi',
            label: t('filters.labels'),
            placeHolderText: t('filters.labelsPlaceholder'),
            values: [
              ...Array.from(
                new Set(
                  (applicationsData ? applicationsData : [])
                    .map(application =>
                      getCombinedLabelsForFilter(
                        application?.labels,
                        application?.discovered_labels
                      )
                    )
                    .flat()
                )
              ),
            ],
          },
        ]}
        onRefresh={() => refreshData()}
        leftInlineMultiSelectFilter={leftInlineMultiSelectFilter}
      />
    );
  };

  const renderServicesFilter = () => {
    return (
      <FindAndFilterBar
        data={servicesData ? servicesData : []}
        filteredDataCallback={data =>
          setFilteredServiceData(data as ServiceData[] | [])
        }
        persistFilter
        filteredData={
          servicesData
            ? filterApplied.length > 0
              ? filteredServiceData
              : servicesData
            : null
        }
        filtersApplied={filterApplied as any}
        filtersAppliedCallback={data => setFilterApplied(data)}
        filters={[
          {
            key: 'application_name',
            type: 'single',
            label: t('filters.application_name'),
            placeHolderText: t('filters.applicationsPlaceholder'),
            values: serviceFilterByApp || [],
          },
        ]}
        onRefresh={() => refreshData()}
        leftInlineMultiSelectFilter={leftInlineMultiSelectFilter}
      />
    );
  };

  const setPageChange = (pageData: { page: number; pageSize: number }) => {
    setPageNumber(pageData.page);
    setPageSize(pageData.pageSize);
  };

  useEffect(() => {
    if (state?.resourceType === 'APPLICATION') {
      setShowRegisterTearsheet(true);
    }
  }, [state]);

  useEffect(() => {
    pageViewed('Applications');
    toggleAppDataLoading(true);
    refreshData();
  }, []);

  useEffect(() => {
    customCreatedApplicationData();
  }, [customApplicationsData]);

  useEffect(() => {
    fetchDeploymentData();
    fetchPartitionsEnvData();
    fetchAllClustersPartitions();
  }, []);

  //fetching all Deployment Environments data for dislaying name in the Tooltip in the card view
  async function fetchDeploymentData() {
    try {
      const response = await getDeploymentEnvs();
      setAllDepEnvs(response);
    } catch (error) {
      console.error(error);
    }
  }

  //fetching all Partitions data for each cluster for dislaying name in the Tooltip
  const fetchAllClustersPartitions = async () => {
    const clusters = allDepEnvs;
    if (!Array.isArray(clusters) || clusters.length === 0) {
      return [];
    }
    try {
      const partitionsPromises = clusters.map(async cluster => {
        const partitionsData = await getPartitions(cluster.resource_id);
        return partitionsData.partitions[0] || [];
      });
      const results = await Promise.all(partitionsPromises);

      setClusterPartitions(results.flat());
    } catch (error) {
      console.log(error);
    }
  };

  async function fetchPartitionsEnvData() {
    try {
      const response = await getPartitions();
      setAllPartitions(response);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    Array.isArray(applicationsData)
      ? toggleAppDataLoading(false)
      : toggleAppDataLoading(true);
  }, [applicationsData]);

  const handleTableSort = (
    data: { id: string; text: string },
    sortDirection: 'ASC' | 'DESC' | 'NONE'
  ) => {
    setSortDirection(sortDirection);
    setSortKey(data.id);
  };

  const learnMore = {
    url:
      navigator.language === 'fr' ||
      navigator.language === 'fr-FR' ||
      navigator.language === 'fr-BE' ||
      navigator.language === 'fr-CA' ||
      navigator.language === 'fr-CH'
        ? 'https://ibm.biz/mesh-apps-fr'
        : 'https://ibm.biz/mesh-apps',
    label: t('learnMore'),
  };

  return (
    <div className='applications-container resources-container'>
      <Header
        title={t('header')}
        subTitle={t('subheader')}
        learnMoreLink={learnMore}
        breadcrumbs={[
          {
            url: '/',
            label: t('home'),
          },
        ]}
        actions={[
          {
            kind: 'primary',
            onClick: () => {
              trackButtonClicked(
                analyticsData['Applications'].events.registerAppBtn.props,
                analyticsData['Applications'].events.registerAppBtn.event
              );
              setShowRegisterTearsheet(true);
            },
            text: t('registerExternalApplication'),
            disabled: appDataLoading,
          },
        ]}
      />
      <div className='page-content'>
        <div className='application-services-list-tabs'>
          <Tabs>
            <TabList contained>
              <Tab>
                {applicationsData
                  ? `${t('applicationHeader')} (${applicationsData?.length})`
                  : `${t('applicationHeader')}`}
              </Tab>
              <Tab>
                {servicesData
                  ? `${t('sevicesHeader')} (${servicesData?.length})`
                  : `${t('sevicesHeader')}`}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <div className='table-body'>
                  {!permissionMap['applications'] ? (
                    <Error403Card />
                  ) : error500 ? (
                    <Error500Card />
                  ) : (
                    <ApplicationsTable
                      currentPageNumber={currentPageNumber}
                      currentPageSize={currentPageSize}
                      sortRows={(
                        data: { id: string; text: string },
                        direction: 'ASC' | 'DESC' | 'NONE'
                      ) => handleTableSort(data, direction)}
                      onPageChange={(pageData: any) => setPageChange(pageData)}
                      rows={
                        Array.isArray(applicationsData)
                          ? filterApplied.length > 0
                            ? sortData(filteredData, sortKey, sortDirection)
                            : sortData(applicationsData, sortKey, sortDirection)
                          : null
                      }
                      elementCount={
                        applicationsData
                          ? filterApplied.length > 0
                            ? filteredData.length
                            : applicationsData.length
                          : 0
                      }
                      filteredDataSet={
                        applicationsData
                          ? filterApplied.length > 0
                            ? filteredData
                            : applicationsData
                          : null
                      }
                      filteredDataCallback={data => {
                        data && setFilteredData(data as ApplicationData[] | []);
                        setPageNumber(1);
                      }}
                      data={applicationsData}
                      allDepEnvs={allDepEnvs}
                      clusterPartitions={clusterPartitions}
                      filtersSelected={filterApplied as any}
                      filtersAppliedCallback={data => {
                        setFilterApplied(data);
                      }}
                      persistFilter
                      filters={[
                        {
                          key: 'applicationGroup',
                          type: 'single',
                          label: t('filters.applicationGroup'),
                          placeHolderText: t(
                            'filters.applicationGroupPlaceholder'
                          ),
                          values: [
                            ...Array.from(
                              new Set(
                                (applicationGroupsData
                                  ? applicationGroupsData.filter(
                                      resource =>
                                        resource.type === 'application'
                                    )
                                  : []
                                ).map(appGroup => appGroup.name)
                              )
                            ),
                          ],
                        },
                        {
                          key: filterTypes.ALLLABELS,
                          type: 'multi',
                          label: t('filters.labels'),
                          placeHolderText: t('filters.labelsPlaceholder'),
                          values: [
                            ...Array.from(
                              new Set(
                                (applicationsData ? applicationsData : [])
                                  .map(application =>
                                    getCombinedLabelsForFilter(
                                      application?.labels,
                                      application?.discovered_labels
                                    )
                                  )
                                  .flat()
                              )
                            ),
                          ],
                        },
                      ]}
                      onRefresh={() => refreshData()}
                      applicationGroupsList={applicationGroupsData}
                      networkSegmentList={networkSegmentsData}
                      error403Flag={!permissionMap['applications']}
                      error500Flag={!!error500}
                      resourceGroupNotAuthorized={
                        !permissionMap['resourceGroup']
                      }
                      networkSegmentNotAuthorized={
                        !permissionMap['networkSegments']
                      }
                      leftInlineMultiSelectFilter={leftInlineMultiSelectFilter}
                      loading={appDataLoading}
                    />
                  )}
                </div>
              </TabPanel>
              <TabPanel>
                <div className='table-body'>
                  {!permissionMap['service'] ? (
                    <Error403Card />
                  ) : error500 ? (
                    <Error500Card />
                  ) : (
                    <ServicesTable
                      applicationsData={applicationsData}
                      loading={appDataLoading}
                      currentPageNumber={currentPageNumber}
                      currentPageSize={currentPageSize}
                      sortRows={(
                        data: { id: string; text: string },
                        direction: 'ASC' | 'DESC' | 'NONE'
                      ) => handleTableSort(data, direction)}
                      onPageChange={(pageData: any) => setPageChange(pageData)}
                      rows={
                        Array.isArray(servicesData)
                          ? filterApplied.length > 0
                            ? sortData(
                                filteredServiceData,
                                sortKey,
                                sortDirection
                              )
                            : sortData(servicesData, sortKey, sortDirection)
                          : null
                      }
                      elementCount={
                        servicesData
                          ? filterApplied.length > 0
                            ? filteredServiceData.length
                            : servicesData.length
                          : 0
                      }
                      filteredDataSet={
                        servicesData
                          ? filteredServiceData.length > 0
                            ? filteredServiceData
                            : servicesData
                          : null
                      }
                      filteredDataCallback={data => {
                        data &&
                          setFilteredServiceData(data as ServiceData[] | []);

                        setPageNumber(1);
                      }}
                      data={servicesData}
                      filtersSelected={filterApplied as any}
                      filtersAppliedCallback={data => {
                        setFilterApplied(data);
                      }}
                      persistFilter
                      filters={[
                        {
                          key: 'application_name',
                          type: 'single',
                          label: t('filters.applications'),
                          placeHolderText: t('filters.applicationsPlaceholder'),

                          values: [
                            ...Array.from(
                              new Set(
                                customApplicationsData?.map(
                                  application => application.name
                                )
                              )
                            ),
                          ],
                        },
                      ]}
                      onRefresh={() => refreshData()}
                      serviceFilterByAppList={serviceFilterByApp}
                      networkSegmentList={networkSegmentsData}
                      error403Flag={!permissionMap['applications']}
                      error500Flag={!!error500}
                      networkSegmentNotAuthorized={
                        !permissionMap['networkSegments']
                      }
                      leftInlineMultiSelectFilter={leftInlineMultiSelectFilter}
                    />
                  )}
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      </div>

      {showRegisterTearsheet && (
        <RegisterExternalService
          applicationsData={applicationsData}
          applicationData={null}
          resourceGroupData={applicationGroupsData}
          networkSegmentData={networkSegmentsData}
          open={showRegisterTearsheet}
          onClose={() => closeRegisterTearsheet()}
          successCallback={() => createdService()}
          mode={'addFromContainerPage'}
        />
      )}
    </div>
  );
};

export default ApplicationsContainer;
